<template>
  <div class="popup" v-if="isOpen" style="z-index: 10001;">
    <div class="popup-content">
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="font-bold mb-0">{{ $t('account.PERSONAL_INFO') }}</h4>
        <div class="btn-close" @click="closePopup">
          <b-icon icon="x" style="color: gray;" scale="1.6"></b-icon>
        </div>
      </div>

      <div class="personal-info-container mt-3">
        <Form ref="observer" v-slot="{ handleSubmit }">
          <b-form
            id="personal-info-form"
            ref="form"
            @submit.prevent="handleSubmit(updateKycPhase2Info)"
            class="cls-custom-form-group-field extend-invalid"
          >
            <!-- OCCUPATION -->
            <div class="form-group-parent">
              <Field
                :key="$i18n.locale"
                :model-value="form.occupation_id"
                @update:modelValue="updateField('occupation_id', $event)"
                :name="$t('account.OCCUPATION')"
                :rules="{ required: true }"
                v-slot="ctx"
              >
                <b-form-group
                  :label="$t('account.OCCUPATION')"
                  label-for="KYC_OCCUPATION"
                  label-class="custom-label"
                  class="custom-form-group"
                >
                  <!-- Normal select when not "Others" -->
                  <b-form-select
                    v-if="!isOtherOccupation"
                    id="KYC_OCCUPATION"
                    :value="form.occupation_id"
                    v-bind="ctx.field"
                    aria-describedby="input-occupation-feedback"
                    :state="isUpdateAllowed ? getValidationState(ctx.meta) : null"
                    :disabled="!isUpdateAllowed"
                    :class="{ 'readonly-select': !isUpdateAllowed }"
                  >
                    <option :value="null" disabled>{{ $t('account.OCCUPATION') }}</option>
                    <option v-for="occupation in occupations" :key="occupation.id" :value="occupation.id">
                      {{ occupation.name }}
                    </option>
                  </b-form-select>
                  <!-- Custom inline input when "Others" selected -->
                  <div v-else class="others-inline-wrapper" :class="{ 'is-invalid': getValidationState(ctx.meta) === false, 'is-disabled': !isUpdateAllowed }">
                    <input
                      type="text"
                      class="others-inline-input"
                      :value="form.occupation_name"
                      @input="updateField('occupation_name', $event.target.value)"
                      :placeholder="$t('account.ENTER_OCCUPATION')"
                      :disabled="!isUpdateAllowed"
                    />
                    <select
                      class="others-hidden-select"
                      :value="form.occupation_id"
                      @change="updateField('occupation_id', Number($event.target.value))"
                      :disabled="!isUpdateAllowed"
                    >
                      <option v-for="occupation in occupations" :key="occupation.id" :value="occupation.id">
                        {{ occupation.name }}
                      </option>
                    </select>
                  </div>
                </b-form-group>
                <b-form-invalid-feedback id="input-occupation-feedback">
                  {{ ctx.errors[0] }}
                </b-form-invalid-feedback>
              </Field>
              <!-- Hidden validation for occupation_name -->
              <Field
                v-if="isOtherOccupation"
                :key="$i18n.locale + '-occupation-name'"
                :model-value="form.occupation_name"
                name="occupation_name"
                :rules="{ required: isOtherOccupation }"
                v-slot="{ errors }"
              >
                <b-form-invalid-feedback v-if="errors.length" :state="false" class="d-block">
                  {{ $t('VALIDATION.FIELD_REQUIRED', { field: $t('account.OCCUPATION') }) }}
                </b-form-invalid-feedback>
              </Field>
            </div>

            <!-- JOB POSITION -->
            <div class="form-group-parent">
              <Field
                :key="$i18n.locale"
                :model-value="form.job_position_id"
                @update:modelValue="updateField('job_position_id', $event)"
                :name="$t('account.JOB_POSITION')"
                :rules="{ required: true }"
                v-slot="ctx"
              >
                <b-form-group
                  :label="$t('account.JOB_POSITION')"
                  label-for="KYC_JOB_POSITION"
                  label-class="custom-label"
                  class="custom-form-group"
                >
                  <b-form-select
                    id="KYC_JOB_POSITION"
                    :value="form.job_position_id"
                    v-bind="ctx.field"
                    aria-describedby="input-job-position-feedback"
                    :state="isUpdateAllowed ? getValidationState(ctx.meta) : null"
                    :disabled="!isUpdateAllowed"
                    :class="{ 'readonly-select': !isUpdateAllowed }"
                  >
                    <option :value="null" disabled>{{ $t('account.JOB_POSITION') }}</option>
                    <option v-for="jp in jobPositions" :key="jp.id" :value="jp.id">
                      {{ jp.name }}
                    </option>
                  </b-form-select>
                </b-form-group>
                <b-form-invalid-feedback id="input-job-position-feedback">
                  {{ ctx.errors[0] }}
                </b-form-invalid-feedback>
              </Field>
            </div>

            <!-- MONTHLY INCOME -->
            <div class="form-group-parent">
              <Field
                :key="$i18n.locale"
                :model-value="form.monthly_income_id"
                @update:modelValue="updateField('monthly_income_id', $event)"
                :name="$t('account.MONTHLY_INCOME')"
                :rules="{ required: true }"
                v-slot="ctx"
              >
                <b-form-group
                  :label="$t('account.MONTHLY_INCOME')"
                  label-for="KYC_MONTHLY_INCOME"
                  label-class="custom-label"
                  class="custom-form-group"
                >
                  <b-form-select
                    id="KYC_MONTHLY_INCOME"
                    :value="form.monthly_income_id"
                    v-bind="ctx.field"
                    aria-describedby="input-monthly-income-feedback"
                    :state="isUpdateAllowed ? getValidationState(ctx.meta) : null"
                    :disabled="!isUpdateAllowed"
                    :class="{ 'readonly-select': !isUpdateAllowed }"
                  >
                    <option :value="null" disabled>{{ $t('account.MONTHLY_INCOME') }}</option>
                    <option v-for="mi in monthlyIncomes" :key="mi.id" :value="mi.id">
                      {{ mi.name }}
                    </option>
                  </b-form-select>
                </b-form-group>
                <b-form-invalid-feedback id="input-monthly-income-feedback">
                  {{ ctx.errors[0] }}
                </b-form-invalid-feedback>
              </Field>
            </div>

            <!-- SOURCE OF INCOME -->
            <div class="form-group-parent">
              <Field
                :key="$i18n.locale"
                :model-value="form.source_of_income_id"
                @update:modelValue="updateField('source_of_income_id', $event)"
                :name="$t('account.SOURCE_OF_INCOME')"
                :rules="{ required: true }"
                v-slot="ctx"
              >
                <b-form-group
                  :label="$t('account.SOURCE_OF_INCOME')"
                  label-for="KYC_SOURCE_OF_INCOME"
                  label-class="custom-label"
                  class="custom-form-group"
                >
                  <b-form-select
                    id="KYC_SOURCE_OF_INCOME"
                    :value="form.source_of_income_id"
                    v-bind="ctx.field"
                    aria-describedby="input-source-income-feedback"
                    :state="isUpdateAllowed ? getValidationState(ctx.meta) : null"
                    :disabled="!isUpdateAllowed"
                    :class="{ 'readonly-select': !isUpdateAllowed }"
                  >
                    <option :value="null" disabled>{{ $t('account.SOURCE_OF_INCOME') }}</option>
                    <option v-for="soi in sourceOfIncomes" :key="soi.id" :value="soi.id">
                      {{ soi.name }}
                    </option>
                  </b-form-select>
                </b-form-group>
                <b-form-invalid-feedback id="input-source-income-feedback">
                  {{ ctx.errors[0] }}
                </b-form-invalid-feedback>
              </Field>
            </div>

            <!-- COMPANY NAME -->
            <div class="form-group-parent">
              <Field
                :key="$i18n.locale + '-' + isCompanyNameOptional"
                :model-value="form.company_name"
                @update:modelValue="updateField('company_name', $event)"
                :name="$t('account.COMPANY_BUSINESS_NAME')"
                :rules="{ required: !isCompanyNameOptional }"
                v-slot="ctx"
              >
                <b-form-group
                  :label="$t('account.COMPANY_BUSINESS_NAME') + (isCompanyNameOptional ? ' (' + $t('OPTIONAL') + ')' : '')"
                  label-for="ACCOUNT_COMPANY_BUSINESS_NAME"
                  label-class="custom-label"
                  class="custom-form-group"
                >
                  <b-form-input
                    id="ACCOUNT_COMPANY_BUSINESS_NAME"
                    v-bind="ctx.field"
                    aria-describedby="input-company-name-feedback"
                    :state="isUpdateAllowed ? getValidationState(ctx.meta) : null"
                    :disabled="!isUpdateAllowed"
                  />
                </b-form-group>
                <b-form-invalid-feedback id="input-company-name-feedback">
                  {{ ctx.errors[0] }}
                </b-form-invalid-feedback>
              </Field>
            </div>

            <!-- COMPANY ADDRESS -->
            <div class="form-group-parent">
              <Field
                :key="$i18n.locale + '-' + isCompanyAddressOptional"
                :model-value="form.company_address"
                @update:modelValue="updateField('company_address', $event)"
                :name="$t('account.COMPANY_BUSINESS_ADDRESS')"
                :rules="{ required: !isCompanyAddressOptional }"
                v-slot="ctx"
              >
                <b-form-group
                  :label="$t('account.COMPANY_BUSINESS_ADDRESS') + (isCompanyAddressOptional ? ' (' + $t('OPTIONAL') + ')' : '')"
                  label-for="ACCOUNT_COMPANY_BUSINESS_ADDRESS"
                  label-class="custom-label"
                  class="custom-form-group"
                >
                  <b-form-input
                    id="ACCOUNT_COMPANY_BUSINESS_ADDRESS"
                    v-bind="ctx.field"
                    aria-describedby="input-company-address-feedback"
                    :state="isUpdateAllowed ? getValidationState(ctx.meta) : null"
                    :disabled="!isUpdateAllowed"
                  />
                </b-form-group>
                <b-form-invalid-feedback id="input-company-address-feedback">
                  {{ ctx.errors[0] }}
                </b-form-invalid-feedback>
              </Field>
            </div>

            <!-- INDUSTRY -->
            <div class="form-group-parent">
              <Field
                :key="$i18n.locale"
                :model-value="form.industry_sector_id"
                @update:modelValue="updateField('industry_sector_id', $event)"
                :name="$t('account.INDUSTRY')"
                :rules="{ required: true }"
                v-slot="ctx"
              >
                <b-form-group
                  :label="$t('account.INDUSTRY')"
                  label-for="KYC_INDUSTRY"
                  label-class="custom-label"
                  class="custom-form-group"
                >
                  <!-- Normal select when not "Others" -->
                  <b-form-select
                    v-if="!isOtherIndustry"
                    id="KYC_INDUSTRY"
                    :value="form.industry_sector_id"
                    v-bind="ctx.field"
                    aria-describedby="input-industry-feedback"
                    :state="isUpdateAllowed ? getValidationState(ctx.meta) : null"
                    :disabled="!isUpdateAllowed"
                    :class="{ 'readonly-select': !isUpdateAllowed }"
                  >
                    <option :value="null" disabled>{{ $t('account.INDUSTRY') }}</option>
                    <option v-for="ind in industrySectors" :key="ind.id" :value="ind.id">
                      {{ ind.name }}
                    </option>
                  </b-form-select>
                  <!-- Custom inline input when "Others" selected -->
                  <div v-else class="others-inline-wrapper" :class="{ 'is-invalid': getValidationState(ctx.meta) === false, 'is-disabled': !isUpdateAllowed }">
                    <input
                      type="text"
                      class="others-inline-input"
                      :value="form.industry_name"
                      @input="updateField('industry_name', $event.target.value)"
                      :placeholder="$t('account.ENTER_INDUSTRY')"
                      :disabled="!isUpdateAllowed"
                    />
                    <select
                      class="others-hidden-select"
                      :value="form.industry_sector_id"
                      @change="updateField('industry_sector_id', Number($event.target.value))"
                      :disabled="!isUpdateAllowed"
                    >
                      <option v-for="ind in industrySectors" :key="ind.id" :value="ind.id">
                        {{ ind.name }}
                      </option>
                    </select>
                  </div>
                </b-form-group>
                <b-form-invalid-feedback id="input-industry-feedback">
                  {{ ctx.errors[0] }}
                </b-form-invalid-feedback>
              </Field>
              <!-- Hidden validation for industry_name -->
              <Field
                v-if="isOtherIndustry"
                :key="$i18n.locale + '-industry-name'"
                :model-value="form.industry_name"
                name="industry_name"
                :rules="{ required: isOtherIndustry }"
                v-slot="{ errors }"
              >
                <b-form-invalid-feedback v-if="errors.length" :state="false" class="d-block">
                  {{ $t('VALIDATION.FIELD_REQUIRED', { field: $t('account.INDUSTRY') }) }}
                </b-form-invalid-feedback>
              </Field>
            </div>
          </b-form>
        </Form>

        <div class="d-flex flex-row justify-content-center mt-4 mb-3">
          <b-button v-if="isUpdateAllowed" class="btn-main px-5 py-2" type="submit" variant="none" form="personal-info-form" :disabled="saveDisabled">
            {{ $t('account.SAVE_INFORMATION') }}
          </b-button>
          <b-button v-if="!isUpdateAllowed" class="btn-main px-5 py-2" variant="none" @click="closePopup">
            {{ $t('common.OK') }}
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Field, Form } from 'vee-validate'
import accountService from "@/services/account.service"

export default {
  components: { Field, Form },
  data() {
    return {
      isOpen: false,
      occupations: [],
      jobPositions: [],
      monthlyIncomes: [],
      sourceOfIncomes: [],
      industrySectors: [],
      form: {
        occupation_id: null,
        job_position_id: null,
        monthly_income_id: null,
        source_of_income_id: null,
        company_name: null,
        company_address: null,
        industry_sector_id: null,
        occupation_name: null,
        industry_name: null,
      },
    }
  },
  async mounted() {
    this.initFormFromStore()
    await this.getKycPhase2Config()
  },
  computed: {
    userProfile() {
      return this.$store.getters.userProfile
    },
    kycPhase2Info() {
      return this.userProfile?.kyc_phase_2 || {}
    },
    isUpdateAllowed() {
      return this.kycPhase2Info?.is_update_required === true
    },
    saveDisabled() {
      return !this.isUpdateAllowed
    },
    isOtherOccupation() {
      if (!this.occupations.length || this.form.occupation_id == null) return false
      const selected = this.occupations.find(o => o.id === this.form.occupation_id)
      if (!selected) return false
      const name = (selected.name || '').toLowerCase().trim()
      return name === 'others' || name === 'lainnya'
    },
    isOtherIndustry() {
      if (!this.industrySectors.length || this.form.industry_sector_id == null) return false
      const selected = this.industrySectors.find(i => i.id === this.form.industry_sector_id)
      if (!selected) return false
      const name = (selected.name || '').toLowerCase().trim()
      return name === 'others' || name === 'lainnya'
    },
    selectedOccupation() {
      if (!this.occupations.length || this.form.occupation_id == null) return null
      return this.occupations.find(o => o.id === this.form.occupation_id) || null
    },
    isCompanyNameOptional() {
      return this.selectedOccupation?.company_name_optional === 1 || this.selectedOccupation?.company_name_optional === true
    },
    isCompanyAddressOptional() {
      return this.selectedOccupation?.company_address_optional === 1 || this.selectedOccupation?.company_address_optional === true
    },
  },
  watch: {
    kycPhase2Info: {
      deep: true,
      handler() {
        this.initFormFromStore()
      }
    }
  },
  methods: {
    openPopup() {
      this.isOpen = true
      this.initFormFromStore()
    },
    closePopup() {
      this.isOpen = false
    },
    initFormFromStore() {
      const src = this.kycPhase2Info || {}
      this.form = {
        occupation_id: src.occupation_id ?? null,
        job_position_id: src.job_position_id ?? null,
        monthly_income_id: src.monthly_income_id ?? null,
        source_of_income_id: src.source_of_income_id ?? null,
        company_name: src.company_name ?? null,
        company_address: src.company_address ?? null,
        industry_sector_id: src.industry_sector_id ?? null,
        occupation_name: src.occupation_name ?? null,
        industry_name: src.industry_name ?? null,
      }
    },
    async getKycPhase2Config() {
      const res = await accountService.getKycPhase2Config()
      if (res && res.data) {
        this.occupations = res.data.occupations
        this.jobPositions = res.data.job_positions
        this.monthlyIncomes = res.data.monthly_incomes
        this.sourceOfIncomes = res.data.source_of_incomes
        this.industrySectors = res.data.industry_sectors
      }
    },
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },
    isOtherOccupationForId(id) {
      if (!this.occupations.length || id == null) return false
      const selected = this.occupations.find(o => o.id === id)
      if (!selected) return false
      const name = (selected.name || '').toLowerCase().trim()
      return name === 'others' || name === 'lainnya'
    },
    isOtherIndustryForId(id) {
      if (!this.industrySectors.length || id == null) return false
      const selected = this.industrySectors.find(i => i.id === id)
      if (!selected) return false
      const name = (selected.name || '').toLowerCase().trim()
      return name === 'others' || name === 'lainnya'
    },
    updateField(field, value) {
      if (!this.isUpdateAllowed) return
      if (this.form[field] !== value) {
        const updates = { [field]: value }
        // Clear occupation_name when switching away from "Others"
        if (field === 'occupation_id' && !this.isOtherOccupationForId(value)) {
          updates.occupation_name = null
        }
        // Clear industry_name when switching away from "Others"
        if (field === 'industry_sector_id' && !this.isOtherIndustryForId(value)) {
          updates.industry_name = null
        }
        this.form = { ...this.form, ...updates }
      }
    },
    async updateKycPhase2Info() {
      if (!this.isUpdateAllowed) return
      await accountService.updateKycPhase2Info({ ...this.form })
      await this.$store.dispatch('refreshUserProfile')
      this.$emit('saved')
      this.closePopup()
    },
  },
}
</script>

<style lang="scss" scoped>
.popup {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, .5);
  display: flex;
  justify-content: center;
  align-items: center;

  .popup-content {
    position: relative;
    background: #fff;
    padding: 10px 30px;
    border-radius: 20px;
    width: 95%;
    max-height: 90vh;
    overflow: auto;

    @media (min-width: 600px) {
      width: 80%;
    }
    @media (min-width: 800px) {
      width: 70%;
    }
    @media (min-width: 900px) {
      width: 65%;
    }
    @media (min-width: 1100px) {
      width: 36%;
    }

    .btn-close {
      background-color: #ddd;
      border-radius: 50%;
      width: 30px;
      height: 30px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
    }
  }
}

/* inner form styling (same as your page component) */
.personal-info-container {
  .content {
    padding: 0;
    box-shadow: none;
    border-radius: 0;
  }

  /* read-only selects look */
  .readonly-select.custom-select,
  .readonly-select.custom-select:disabled {
    background-color: #fff !important;
    color: #3e3e3e;
    opacity: 1;
    cursor: default;
    background-image: none !important;
    padding-right: .75rem;
    appearance: none;
  }

  .readonly-select.custom-select:disabled:focus {
    box-shadow: none;
  }

  /* read-only inputs look */
  .form-control:disabled,
  .form-control[disabled] {
    background-color: #fff !important;
    color: #3e3e3e !important;
    opacity: 1 !important;
    cursor: default !important;
    background-image: none !important;
  }

  .form-control:disabled:focus {
    box-shadow: none !important;
  }

  .others-inline-wrapper {
    display: flex;
    align-items: center;
    border: 0 solid #ced4da;
    border-radius: 0.25rem;
    background-color: #fff;
    padding: 0 0 0 8rem;
    height: calc(1.5em + 0.75rem - 4px);

    &.is-invalid {
      border-color: #dc3545;
    }

    &.is-disabled {
      background-color: #fff;
      cursor: default;
    }

    .others-inline-input {
      flex: 1;
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
      background: transparent;
      min-width: 0;
      padding: 0.375rem 0;
      color: #495057;
      height: auto;

      &::placeholder {
        color: #6c757d;
      }

      &:focus {
        border: none !important;
        outline: none !important;
        box-shadow: none !important;
      }

      &:disabled {
        background: transparent;
        cursor: default;
      }
    }

    .others-hidden-select {
      flex-shrink: 0;
      border: none;
      cursor: pointer;
      padding: 0.375rem 1rem;
      color: transparent;
      width: 2rem;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      font-size: 0;
      background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3e%3cpath fill='%23343a40' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e") no-repeat right 0.5rem center;
      background-size: 8px 10px;

      option {
        color: #495057;
        font-size: 1rem;
      }

      &:disabled {
        background-image: none;
        cursor: default;
      }
    }
  }
}
</style>
